
import {useState, useCallback, useRef, useEffect} from 'react';
import ConfirmDialog from '@/components/ConfirmDialog';
import {getMorphoService, serviceNames} from '@/rte/editor';
import {useHistory} from 'react-router-dom';
import * as store from '@/store';
import {routerPublicPath} from '@/config/routers';
import {reportCloseCommentSidebar} from '@/buried';
import {getConfigs} from '@/kuSdk/view/DocViewAll/utils/configs';

// const uploadingTip = '检测到有尚未上传完成的文件，请检查确认后再离开页面';

// const commentNotPublishedTip = '检测到有尚未发布的内容，请检查确认后再离开页面';

const tip: any = {
    uploadingTip: '检测到有尚未上传完成的文件，请检查确认后再离开页面',
    commentTip: '检测到有尚未发布的评论，请检查确认后再离开页面',
    commentUploadingTip: '检查到有尚未上传完成的文件，请检查确认后再操作',
};

const modeType = {
    confirm: 1,
    cancel: 2,
};

const docModeType = {
    preview: 1, // 预览态
    edit: 2, // 编辑态
    meeting: 3, // 协作模式
};

const WindowType = {
    uploadingTip: 'image',
    commentTip: 'comment',
};

// triggerBlock 是否触发路由调整(页面卸载)拦截, 默认false不触发, 保证事件只绑定一次
export const useVerifyEditorDialog = (triggerBlock = false, mode: string) => {
    const history = useHistory();
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const callbackRef = useRef<any>();
    // 存一下 被拦截的路由
    const [desiredPath, setDesiredPath] = useState('');
    // 事件来源 verifying验证方法 listenIn监听路由
    const [eventSource, setEventSource] = useState('');
    // 判断检测的图片上传还是评论未发布 uploadingTip是图片上传  commentTip是评论
    const [tipType, setTipType] = useState('');

    const verifiedEvents = useCallback((callBack: () => void, closeTheSideWindow = false) => {
        return () => {
            try {
                const {current: editor} = store.getEditorRef();
                const {current: commentEditor} = store.getCommentEditorRef();
                const commentService = getMorphoService(serviceNames.ICommentService);
                // 划线评论是否有评论
                // 如果commentService不存在(不存在的场景包括非文档页)则返回undefined,不走弹窗逻辑
                const hasComment = commentService?.hasUnsavedComments();
                // 只有预览态时候才能拿到底部评论的Editor, 编辑态拿不到
                // hasBottomCommentsView 判断是否有底部评论组件，再去检测是否有底部评论内容，(关闭侧边评论时不检测底部评论)
                const hasBottomCommentsView = mode === 'preview' && !closeTheSideWindow;
                // 底部评论组件是否为空，编辑器没加载完成时commentEditor拿到的是undefine, 直接判为空true(防止弹出弹窗)
                const isBottomCommentEmpty = commentEditor ? commentEditor.isEmpty() : true;
                // isEmpty判断底部评论是否为空，基于hasBottomCommentsView和isBottomCommentEmpty逻辑
                // 没有底部评论组件和关闭侧边评论时，直接认为底部评论为空
                const isEmpty = hasBottomCommentsView ? isBottomCommentEmpty : true;
                // 底部评论或者侧边评论有内容
                if (!isEmpty || hasComment) {
                    setShowDialog(true);
                    setEventSource('verifying');
                    setTipType('commentTip');
                    callbackRef.current = callBack;
                    return;
                }
                // 图片上传
                // closeTheSideWindow关闭侧边评论窗口时不检测图片上传
                // 只有在编辑态时才验证是否有上传中的图片（灰度环境预览态有可能拿线上图片造成编辑器返回getUploadingState错误）
                const isUploading = editor?.getUploadingState();
                if (isUploading && !closeTheSideWindow && mode === 'edit') {
                    setShowDialog(true);
                    setEventSource('verifying');
                    setTipType('uploadingTip');
                    callbackRef.current = callBack;
                    return;
                }
                callBack();
            }
            catch {
                callBack();
            }
        };
    }, [mode]);

    const showCancelDialogWithTipType = useCallback((tipType) => {
        setShowDialog(true);
        setEventSource('showCancelDialog');
        setTipType(tipType);
        callbackRef.current = () => {};
    }, []);

    useEffect(() => {
        // block 返回一个取消监听的函数，所以你可以在组件卸载时调用它
        const unblock = history.block((res: any) => {
            // 你可以在这里增加你的条件判断
            const {current: editor} = store.getEditorRef();
            const isUploading = editor?.getUploadingState();
            // 询问用户是否真的要跳转
            // 只有在编辑态时才验证是否有上传中的图片（灰度环境预览态有可能拿线上图片造成编辑器返回getUploadingState错误）
            if (isUploading && mode === 'edit') {
                setDesiredPath(res.pathname);
                setShowDialog(true);
                setEventSource('listenIn');
                setTipType('uploadingTip');
                return false;
            }
        });

        return () => {
        // 在组件卸载时取消监听
            unblock();
        };
    }, [history, mode]);

    // 关闭或者刷新浏览器
    useEffect(() => {
        const onBeforeUnload = (event: any) => {
            try {
                if (store.getDownloadFileStatus() === 'start') {
                    // 下载附件时，会触发 beforeunload 事件，此时不弹出提示框
                    return;
                }

                if (!(window as any).isSdkPage || getConfigs().beforeUnloadTips) {
                    const {current: editor} = store.getEditorRef();
                    const {current: commentEditor} = store.getCommentEditorRef();
                    const commentService = getMorphoService(serviceNames.ICommentService);
                    // 划线评论是否有评论,
                    // 如果commentService不存在(不存在的场景包括非文档页)则返回undefined,不走弹窗逻辑
                    const hasComment = commentService?.hasUnsavedComments();
                    // 只有预览态时候才能拿到底部评论的Editor,编辑态则底部评论的isEmpty直接为true
                    const isEmpty = mode === 'preview' ? commentEditor?.isEmpty() : true;
                    // 收到编辑器通知后，页面需要刷新不走系统弹窗逻辑
                    const saveCommentReload = store.getData(store.KEYS.SAVECOMMENT_RELOAD);
                    // 底部评论或者侧边评论有内容
                    if ((!isEmpty || hasComment) && !saveCommentReload) {
                        event.returnValue = tip.commentTip;
                        return tip.uploadingTip;
                    }

                    const isUploading = editor?.getUploadingState();
                    // 只有在编辑态时才验证是否有上传中的图片（灰度环境预览态有可能拿线上图片造成编辑器返回getUploadingState错误）
                    if (isUploading && mode === 'edit') {
                        event.returnValue = tip.uploadingTip;
                        return tip.uploadingTip;
                    }
                }

            }
            catch (error) {
                console.log(error);
            }
        };
        if (triggerBlock) {
            window.addEventListener('beforeunload', onBeforeUnload);
        }
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, [triggerBlock, mode]);

    const handDialogConfirm = () => {
        const {current: editor} = store.getEditorRef();
        editor?.destroy();

        setTimeout(() => {
            if (eventSource === 'verifying') {
                // 弹框点击确定时，继续执行之前的方法
                // 方法在验证是否在上传中时，存在了callbackRef中
                callbackRef.current();
            }

            if (eventSource === 'listenIn') {
                history.push(routerPublicPath + desiredPath);
                setDesiredPath('');
            }

            setEventSource('');
            setShowDialog(false);
            setTipType('');
            reportCloseCommentSidebar({
                type: modeType.confirm,
                docMode: mode === 'preview' ? docModeType.preview :
                    (mode === 'edit' ? docModeType.edit : docModeType.meeting),
                WindowType: tipType === 'commentTip' ? WindowType.commentTip : WindowType.uploadingTip,
            });
        }, 800);
    };

    const handDialogCancel = () => {
        setShowDialog(false);
        setTipType('');
        reportCloseCommentSidebar({
            type: modeType.cancel,
            docMode: mode === 'preview' ? docModeType.preview :
                (mode === 'edit' ? docModeType.edit : docModeType.meeting),
            WindowType: tipType === 'commentTip' ? WindowType.commentTip : WindowType.uploadingTip,
        });
    };

    const VerifyEditorDialog = () => {
        if (eventSource === 'showCancelDialog') {
            return (
                <ConfirmDialog
                    showCloseX={false}
                    showHeader={false}
                    visible={showDialog}
                    onConfirm={handDialogCancel}
                    confirmLabel={'关闭'}
                    showCancelButton={false}
                >
                    {tip[tipType]}
                </ConfirmDialog>
            );
        }
        return (
            <ConfirmDialog
                showCloseX={false}
                showHeader={false}
                visible={showDialog}
                onConfirm={handDialogConfirm}
                onCancel={handDialogCancel}
            >
                {tip[tipType]}
            </ConfirmDialog>
        );
    };


    return {
        VerifyEditorDialog,
        verifiedEvents,
        showCancelDialogWithTipType,
    };
};
